import * as THREE from 'three';
import type { BuildingSource, RawDataGroup, RawDataObject, RawDataPoint, RawTriangle } from '@visualizer/types';
import { PreProcessingTypes } from '@visualizer/types';

export type ObstructionTopMode = 'FLAT_FROM_LOWEST' | 'FOLLOWS_TERRAIN';

export type BuildExtrusionParams = {
    polygonXY: Array<{ x: number; y: number }>; // no need to repeat last point
    getTerrainZ: (x: number, y: number) => number;

    height: number; // meters
    elevation: number; // meters, can be 0 for “on ground”, >0 for “bridge”

    topMode: ObstructionTopMode;

    id: number; // temp negative id
    source: BuildingSource;

    groupTypes?: {
        floor: PreProcessingTypes;
        roof: PreProcessingTypes;
        wall: PreProcessingTypes;
    };
};

export function buildExtrudedBuildingGroups({
    polygonXY,
    getTerrainZ,
    height,
    elevation,
    topMode,
    id,
    source,
    groupTypes,
}: BuildExtrusionParams): RawDataGroup[] {
    if (polygonXY.length < 3) return [];

    const resolvedGroupTypes = groupTypes ?? {
        floor: PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR,
        roof: PreProcessingTypes.CUSTOM_BUILDINGS_ROOF,
        wall: PreProcessingTypes.CUSTOM_BUILDINGS_WALL,
    };

    // Convert polygon to Vector2 for triangulation
    const contour2 = polygonXY.map((p) => new THREE.Vector2(p.x, p.y));

    // Triangulate polygon
    const faces = THREE.ShapeUtils.triangulateShape(contour2, []); // returns [a,b,c][] indices

    // Sample terrain + compute base/top Z per vertex
    const zBase = polygonXY.map((p) => getTerrainZ(p.x, p.y) + elevation);
    const zBaseMin = Math.min(...zBase);

    const zTop = topMode === 'FLAT_FROM_LOWEST' ? zBase.map(() => zBaseMin + height) : zBase.map((zb) => zb + height);

    // -------------------------
    // FLOOR object
    // -------------------------
    const floorPoints: RawDataPoint[] = polygonXY.map((p, i) => ({
        x: p.x,
        y: p.y,
        z: zBase[i],
    }));

    const floorTriangles: RawTriangle[] = faces.map(([a, b, c]) => ({
        A: a,
        B: b,
        C: c,
        ID: id,
        buildingSource: source,
    }));

    const floorObj: RawDataObject = {
        points: floorPoints,
        triangles: floorTriangles,
    };

    // -------------------------
    // ROOF object
    // -------------------------
    const roofPoints: RawDataPoint[] = polygonXY.map((p, i) => ({
        x: p.x,
        y: p.y,
        z: zTop[i],
    }));

    // Reverse winding for roof so normals are opposite of floor.
    const roofTriangles: RawTriangle[] = faces.map(([a, b, c]) => ({
        A: a,
        B: c,
        C: b,
        ID: id,
        buildingSource: source,
    }));

    const roofObj: RawDataObject = {
        points: roofPoints,
        triangles: roofTriangles,
    };

    // -------------------------
    // WALLS object
    // -------------------------
    // Build walls with shared indices: for each polygon vertex i,
    // base index = 2*i, top index = 2*i+1
    const wallPoints: RawDataPoint[] = [];
    for (let i = 0; i < polygonXY.length; i++) {
        const p = polygonXY[i];
        wallPoints.push({ x: p.x, y: p.y, z: zBase[i] }); // base
        wallPoints.push({ x: p.x, y: p.y, z: zTop[i] }); // top
    }

    const n = polygonXY.length;
    const wallTriangles: RawTriangle[] = [];
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;

        const b0 = 2 * i;
        const t0 = 2 * i + 1;
        const b1 = 2 * j;
        const t1 = 2 * j + 1;

        // Quad split into two triangles:
        // (b0, b1, t1) and (b0, t1, t0)
        wallTriangles.push({ A: b0, B: b1, C: t1, ID: id, buildingSource: source });
        wallTriangles.push({ A: b0, B: t1, C: t0, ID: id, buildingSource: source });
    }

    const wallObj: RawDataObject = {
        points: wallPoints,
        triangles: wallTriangles,
    };

    // Return groups
    return [
        { type: resolvedGroupTypes.floor, objects: [floorObj] },
        { type: resolvedGroupTypes.roof, objects: [roofObj] },
        { type: resolvedGroupTypes.wall, objects: [wallObj] },
    ];
}
